class ContactHub extends HTMLElement {
  async connectedCallback() {
    const connection = this.connection = (this.connection || 0) + 1;
    this.routingPromise ||= import(this.dataset.routingUrl);
    this.navigationPromise ||= import(this.dataset.navigationUrl);
    try { [this.routing, this.navigation] = await Promise.all([this.routingPromise, this.navigationPromise]); } catch { return; }
    if (!this.isConnected || this.connection !== connection) return;
    this.controller?.abort();
    const { signal } = (this.controller = new AbortController());
    this.toggle = this.querySelector('.contact-hub__toggle');
    this.panel = this.querySelector('.contact-hub__panel');
    this.heading = this.querySelector('h2');
    this.back = this.querySelector('.contact-hub__back');
    this.body = this.querySelector('.contact-hub__body');
    this.hours = this.querySelector('.contact-hub__hours');
    this.steps = new Map([...this.querySelectorAll('[data-contact-step]')].map(el => [el.dataset.contactStep, el]));
    this.destinations = [...this.querySelectorAll('[data-contact-destinations] a')].map(el => ({
      href: el.href, method: el.dataset.method, branch: el.dataset.branch, label: el.textContent.trim(), audience: el.dataset.audience, fallback: el.dataset.fallback === 'true',
    }));
    this.intents = [...this.querySelectorAll('[data-contact-intent]')];
    for (const option of this.intents) {
      const methods = this.methodsFor(option.dataset);
      option.closest('li').hidden = !methods.length;
      if (option.dataset.flow !== 'billing' && methods.length === 1 && methods[0].routes.length === 1) {
        const link = this.makeLink(methods[0].routes[0], undefined, option.dataset.message);
        link.className = option.className;
        link.prepend(...option.childNodes);
        option.replaceWith(link);
      }
    }
    this.addEventListener('click', event => this.onClick(event), { signal });
    document.addEventListener('click', event => {
      const trigger = event.target.closest('[data-contact-open]');
      if (!event.defaultPrevented && !event.button && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && trigger && this.openIntent(trigger.dataset.contactOpen, trigger)) event.preventDefault();
    }, { signal });
    document.addEventListener('shopify:section:load', () => this.updateTriggers(), { signal });
    this.addEventListener('shopify:block:select', event => {
      this.setOpen(true);
      event.target.querySelector('button[data-contact-intent]')?.click();
    }, { signal });
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.hasAttribute('data-open')) {
        event.preventDefault();
        event.stopPropagation();
        this.setOpen(false, true);
      }
    }, { signal });
    document.addEventListener('pointerdown', event => {
      if (!this.contains(event.target)) this.setOpen(false);
    }, { signal });
    document.addEventListener('focusin', event => {
      if (!this.contains(event.target)) this.setOpen(false);
      this.syncBlocking();
    }, { signal });
    for (const name of ['toggle', 'close']) {
      document.addEventListener(name, () => this.syncBlocking(), { capture: true, signal });
    }
    for (const name of ['cart:opening', 'quick-add:ready']) {
      document.addEventListener(name, () => this.setOpen(false), { signal });
    }
    document.addEventListener('privacy:geometry', () => this.syncBlocking(), { signal });
    for (const name of ['purchase:geometry', 'shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']) {
      document.addEventListener(name, () => this.observeBoundaries(), { signal });
    }
    window.addEventListener('resize', () => this.observeBoundaries(), { passive: true, signal });
    window.addEventListener('scroll', () => { if (this.inlineIntent) this.updateBoundary(); }, { passive: true, signal });
    this.setOpen(false);
    this.hidden = false;
    this.observeBoundaries();
    this.syncBlocking();
    this.updateTriggers();
  }

  disconnectedCallback() {
    this.connection = (this.connection || 0) + 1;
    this.controller?.abort();
    this.footerObserver?.disconnect();
    this.formObserver?.disconnect();
    this.footer = null;
    this.form = null;
    this.motion?.cancel();
    this.pendingOpen?.abort();
    for (const trigger of document.querySelectorAll('button[data-contact-open]')) trigger.disabled = true;
  }

  methodsFor(intent) {
    return ['whatsapp', 'phone'].map(method => ({ method, routes: this.routing.contactRoutes(this.destinations, intent, method) })).filter(item => item.routes.length);
  }

  intentFor(name) {
    return this.intents?.find(option => option.dataset.contactIntent === name || option.dataset.flow === name);
  }

  updateTriggers() {
    for (const trigger of document.querySelectorAll('button[data-contact-open]')) {
      const option = this.intentFor(trigger.dataset.contactOpen);
      trigger.disabled = !option || !this.methodsFor(option.dataset).length;
    }
  }

  openIntent(name, trigger = document.activeElement) {
    const option = this.intentFor(name);
    if (!this.panel || !option || !this.methodsFor(option.dataset).length) return false;
    this.syncBlocking();
    this.updateBoundary();
    if (this.hasAttribute('data-blocked')) return false;
    if (trigger?.closest && !trigger.closest('.store-footer')) {
      this.setOpen(false);
      this.inlineIntent = true;
      this.updateBoundary();
      if (this.hasAttribute('data-boundary-hidden')) { this.setOpen(false); return false; }
      this.returnFocus = trigger;
      this.setAttribute('data-external-open', '');
      this.setAttribute('data-inline-open', '');
      this.inert = false;
      this.setOpen(true, false, option);
      return true;
    }
    const top = this.footer ? window.scrollY + this.footer.getBoundingClientRect().top - window.innerHeight - this.footerClearance - 24 : window.scrollY;
    const reposition = this.hasAttribute('data-boundary-hidden');
    this.setOpen(false);
    this.returnFocus = trigger;
    this.setAttribute('data-external-open', '');
    this.updateBoundary();
    this.inert = false;
    this.setOpen(true, false, option);
    if (reposition) {
      const controller = this.pendingOpen = new AbortController();
      void this.scrollFromFooter(top, controller);
    }
    return true;
  }

  async scrollFromFooter(top, controller) {
    try { await this.navigation.scrollPageTo(top, controller.signal); } catch {}
    finally { if (this.pendingOpen === controller) this.pendingOpen = null; }
  }

  selectIntent(option) {
    this.intent = option.dataset;
    this.message = option.dataset.message || '';
    const methods = this.methodsFor(this.intent);
    if (!methods.length) return;
    this.querySelector('[data-intent-context]').textContent = option.querySelector('[data-intent-label]').textContent;
    if (this.intent.flow === 'billing') {
      this.renderMethods(methods, this.querySelector('[data-billing-options]'));
      this.showStep('billing');
    } else if (methods.length === 1 && methods[0].routes.length > 1) {
      this.showBranches(methods[0].method, 'intent');
    } else {
      this.renderMethods(methods, this.querySelector('[data-method-options]'));
      this.showStep('method', methods.length === 1 ? option.querySelector('[data-intent-label]').textContent : undefined);
    }
  }

  onClick(event) {
    if (event.target.closest('.contact-hub__toggle')) this.setOpen(!this.hasAttribute('data-open'));
    else if (event.target.closest('.contact-hub__close')) this.setOpen(false, true);
    else if (event.target.closest('.contact-hub__back')) {
      this.showStep(this.step === 'branch' ? this.branchParent : 'intent');
    } else if (event.target.closest('[data-contact-intent]')) {
      this.selectIntent(event.target.closest('[data-contact-intent]'));
    } else if (event.target.closest('[data-choose-method]')) {
      this.showBranches(event.target.closest('[data-choose-method]').dataset.chooseMethod, this.step);
    }
  }

  showBranches(method, parent) {
    const routes = this.routing.contactRoutes(this.destinations, this.intent, method);
    this.querySelector('[data-branch-options]').replaceChildren(...routes.map(route => this.makeLink(route, route.label)));
    this.branchParent = parent;
    this.showStep('branch', this.steps.get('branch').dataset[method + 'Title']);
  }

  renderMethods(methods, container) {
    const controls = methods.map(({ method, routes }) => {
      const control = routes.length === 1 ? this.makeLink(routes[0]) : document.createElement('button');
      if (this.intent.flow === 'billing' && methods.length === 1 && routes.length === 1 && method === 'whatsapp') {
        control.className = 'contact-hub__billing';
        control.prepend(this.querySelector('[data-billing-template]').content.cloneNode(true));
        return control;
      }
      if (routes.length > 1) {
        control.type = 'button';
        control.dataset.chooseMethod = method;
      }
      control.className = 'contact-hub__method';
      control.prepend(this.querySelector('[data-method-template="' + method + '"]').content.cloneNode(true));
      if (routes.length > 1) control.append(this.querySelector('[data-forward-icon]').content.cloneNode(true));
      return control;
    });
    container.replaceChildren(...controls);
  }

  makeLink(route, label, message = this.message) {
    const link = document.createElement('a');
    link.href = this.routing.contactHref(route, message || '');
    link.className = 'contact-hub__destination';
    if (label) link.textContent = label;
    if (route.method === 'whatsapp') {
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      const hint = document.createElement('span');
      hint.className = 'visually-hidden';
      hint.textContent = ` (${this.querySelector('[data-external-label]').textContent})`;
      link.append(hint);
    }
    return link;
  }

  showStep(name, title, focus = true) {
    this.motion?.cancel();
    this.step = name;
    for (const [key, el] of this.steps) el.hidden = key !== name;
    this.heading.textContent = title || this.steps.get(name).dataset.title;
    this.back.hidden = name === 'intent';
    if (this.hours) this.hours.hidden = name === 'intent' || name === 'billing';
    this.body.scrollTop = 0;
    if (focus) {
      // A neutral heading announces the new screen without preselecting an action.
      this.heading.focus({ preventScroll: true });
      if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.motion = this.steps.get(name).animate([{ opacity: 0, transform: 'translateY(5px)' }, { opacity: 1, transform: 'none' }], { duration: 160, easing: 'ease-out' });
      }
    }
  }

  setOpen(open, restoreFocus = false, option) {
    this.toggleAttribute('data-open', open);
    this.toggle.setAttribute('aria-expanded', String(open));
    this.panel.inert = !open;
    if (open) {
      if (!option) this.returnFocus = this.toggle;
      if (option) this.selectIntent(option);
      else this.showStep('intent');
    }
    else {
      this.pendingOpen?.abort();
      this.pendingOpen = null;
      this.motion?.cancel();
      this.removeAttribute('data-external-open');
      this.removeAttribute('data-inline-open');
      this.inlineIntent = false;
      this.style.removeProperty('--contact-inline-clearance');
      this.updateBoundary();
      if (restoreFocus) {
        const trigger = this.returnFocus?.isConnected ? this.returnFocus : null;
        const bounds = trigger?.getBoundingClientRect();
        const rendered = bounds?.width > 0 && bounds.height > 0;
        const visible = rendered && bounds.top >= 0 && bounds.left >= 0 && bounds.bottom <= window.innerHeight && bounds.right <= window.innerWidth;
        if (rendered) trigger.focus({ preventScroll: visible });
        else this.toggle.focus({ preventScroll: true });
      }
      this.returnFocus = null;
    }
  }

  syncBlocking() {
    const blocked = document.documentElement.hasAttribute('data-privacy-overlay') || Boolean(document.querySelector('dialog[open], .store-header [data-menu-branch][open], .store-header [data-header-search][open]')) || Boolean(document.querySelector('shopify-account')?.shadowRoot?.querySelector('dialog[open]'));
    this.toggleAttribute('data-blocked', blocked);
    if (blocked) {
      this.pendingOpen?.abort();
      this.pendingOpen = null;
    }
    this.updateAvailability();
  }

  updateAvailability() {
    const unavailable = this.hasAttribute('data-blocked') || this.hasAttribute('data-boundary-hidden');
    if (unavailable && this.hasAttribute('data-open')) {
      const focused = this.contains(document.activeElement);
      this.setOpen(false);
      if (focused && !this.hasAttribute('data-blocked')) this.footer?.querySelector('a[href]')?.focus({ preventScroll: true });
    }
    this.inert = unavailable;
  }

  updateBoundary() {
    const footerTop = this.footer?.getBoundingClientRect().top ?? Infinity;
    if (this.inlineIntent) this.style.setProperty('--contact-inline-clearance', `${Math.max(0, window.innerHeight - footerTop) + 12}px`);
    const inlineSpace = this.inlineIntent && Math.min(window.innerHeight, footerTop) > (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-occupied')) || 0) + 192;
    const footerIntent = this.hasAttribute('data-external-open') && !this.inlineIntent;
    this.toggleAttribute('data-boundary-hidden', !footerIntent && !inlineSpace && footerTop <= window.innerHeight + this.footerClearance);
    this.updateAvailability();
  }

  observeBoundaries() {
    this.style.setProperty('--contact-viewport-width', `${document.documentElement.clientWidth}px`);
    const footer = document.querySelector('.store-footer');
    const capsule = document.querySelector('.product-page .product-purchase-capsule');
    const boundaryControl = capsule?.offsetHeight ? capsule : this.toggle;
    const footerClearance = boundaryControl.offsetHeight + (parseFloat(getComputedStyle(capsule?.offsetHeight ? capsule : this).bottom) || 0);
    if (footer !== this.footer || footerClearance !== this.footerClearance) {
      this.footerObserver?.disconnect();
      this.footer = footer;
      this.footerClearance = footerClearance;
      this.removeAttribute('data-boundary-hidden');
      if (footer && 'IntersectionObserver' in window) {
        // Use the commerce control's footprint on PDP while retaining independent visibility.
        this.footerObserver = new IntersectionObserver(() => this.updateBoundary(), { rootMargin: `0px 0px ${footerClearance}px 0px` });
        this.footerObserver.observe(footer);
      }
      this.updateBoundary();
    }
    const form = document.querySelector('.product-page .product-page__form');
    const height = window.innerHeight;
    const footprint = this.toggle.offsetHeight + 36;
    if (form === this.form && height === this.viewportHeight && window.innerWidth === this.viewportWidth && footprint === this.footprint) return;
    this.formObserver?.disconnect();
    this.form = form;
    this.viewportHeight = height;
    this.viewportWidth = window.innerWidth;
    this.footprint = footprint;
    this.style.removeProperty('--contact-clearance');
    if (form && 'IntersectionObserver' in window) {
      // Only observe the fixed launcher's bottom strip; moving the launcher cannot retrigger this boundary.
      this.formObserver = new IntersectionObserver(([entry]) => {
        this.style.setProperty('--contact-clearance', entry.isIntersecting ? `${Math.ceil(entry.boundingClientRect.height) + footprint}px` : '0px');
      }, { rootMargin: `${-Math.max(0, height - footprint)}px 0px 0px 0px` });
      this.formObserver.observe(form);
    }
  }
}

if (!customElements.get('contact-hub')) customElements.define('contact-hub', ContactHub);
